import { NSARouterMakerResult } from "namirasoft-account-react";
import { FailoverFullRow, GatewayFullRow, LoadBalancerFullRow, ModelRow, RaceFullRow, RouterFullRow } from "namirasoft-inference";
import { Component, ReactNode } from "react";
import { InferenceRouterProps } from "../InferenceRouterProps";
import { InferenceRoutes } from "../InferenceRoutes";
import { InferencerTreeNode } from "./InferencerTree";

export interface InferencerJungleLoaderProps extends NSARouterMakerResult, InferenceRouterProps
{
    pageSize?: number;
    render: (args: { roots: InferencerTreeNode[] | null; loading: boolean; error: string | null }) => ReactNode;
}

interface InferencerJungleLoaderState
{
    loading: boolean;
    error: string | null;
    roots: InferencerTreeNode[] | null;
}

const DEFAULT_PAGE = 200;
const MAX_DEPTH = 8;

function key(type: string, id: string): string { return `${type}:${id}`; }

export default class InferencerJungleLoader extends Component<InferencerJungleLoaderProps, InferencerJungleLoaderState>
{
    override state: InferencerJungleLoaderState = { loading: false, error: null, roots: null };
    private counter = 0;

    override componentDidMount(): void { this.load(); }

    private nextId(): string { this.counter += 1; return `j${this.counter}`; }

    private async load(): Promise<void>
    {
        this.setState({ loading: true, error: null, roots: null });
        this.counter = 0;
        let servers = this.props.servers;
        let pageSize = this.props.pageSize ?? DEFAULT_PAGE;
        try
        {
            let [models, lbs, fos, races, routers, gateways] = await Promise.all([
                servers.inference.model.List([], 1, pageSize, [], null),
                servers.inference.load_balancer.List([], 1, pageSize, [], null),
                servers.inference.failover.List([], 1, pageSize, [], null),
                servers.inference.race.List([], 1, pageSize, [], null),
                servers.inference.router.List([], 1, pageSize, [], null),
                servers.inference.gateway.List([], 1, pageSize, [], null),
            ]);

            let [lbFulls, foFulls, raceFulls, routerFulls, gatewayFulls] = await Promise.all([
                Promise.all(lbs.rows.map((r) => servers.inference.load_balancer.Get(r.id))),
                Promise.all(fos.rows.map((r) => servers.inference.failover.Get(r.id))),
                Promise.all(races.rows.map((r) => servers.inference.race.Get(r.id))),
                Promise.all(routers.rows.map((r) => servers.inference.router.Get(r.id))),
                Promise.all(gateways.rows.map((r) => servers.inference.gateway.Get(r.id))),
            ]);

            let modelMap = new Map<string, ModelRow>();
            let lbMap = new Map<string, LoadBalancerFullRow>();
            let foMap = new Map<string, FailoverFullRow>();
            let raceMap = new Map<string, RaceFullRow>();
            let routerMap = new Map<string, RouterFullRow>();
            let gatewayMap = new Map<string, GatewayFullRow>();

            for (let m of models.rows as ModelRow[]) modelMap.set(m.id, m);
            for (let r of lbFulls as LoadBalancerFullRow[]) lbMap.set(r.id, r);
            for (let r of foFulls as FailoverFullRow[]) foMap.set(r.id, r);
            for (let r of raceFulls as RaceFullRow[]) raceMap.set(r.id, r);
            for (let r of routerFulls as RouterFullRow[]) routerMap.set(r.id, r);
            for (let r of gatewayFulls as GatewayFullRow[]) gatewayMap.set(r.id, r);

            let referenced = new Set<string>();
            let markRef = (type: string, id: string) => { referenced.add(key(type, id)); };

            for (let lb of lbFulls as LoadBalancerFullRow[])
                for (let t of (lb.targets ?? [])) markRef(t.inferencer_type, t.inferencer_id);
            for (let fo of foFulls as FailoverFullRow[])
                for (let t of (fo.targets ?? [])) markRef(t.inferencer_type, t.inferencer_id);
            for (let race of raceFulls as RaceFullRow[])
                for (let t of (race.targets ?? [])) markRef(t.inferencer_type, t.inferencer_id);
            for (let rt of routerFulls as RouterFullRow[])
            {
                for (let r of (rt.routes ?? [])) markRef(r.inferencer_type, r.inferencer_id);
                if (rt.default_inferencer_id) markRef(rt.default_inferencer_type, rt.default_inferencer_id);
            }
            for (let gw of gatewayFulls as GatewayFullRow[])
                if (gw.inferencer_id) markRef(gw.inferencer_type, gw.inferencer_id);

            let buildTree = (type: string, id: string, depth: number, visited: Set<string>): InferencerTreeNode =>
            {
                let k = key(type, id);
                let node: InferencerTreeNode = { id: this.nextId(), type, entity_id: id, name: id.slice(-6), href: "", children: [] };
                if (visited.has(k) || depth > MAX_DEPTH)
                {
                    node.name = `${type} (recursive)`;
                    return node;
                }
                let nextVisited = new Set(visited);
                nextVisited.add(k);

                switch (type)
                {
                    case "MODEL":
                    {
                        let m = modelMap.get(id);
                        if (m)
                        {
                            node.name = m.name;
                            node.subtitle = `${m.model_company} · ${m.model_name}`;
                            node.href = InferenceRoutes.ModelView(m.id);
                        }
                        break;
                    }
                    case "LOAD_BALANCER":
                    {
                        let lb = lbMap.get(id);
                        if (lb)
                        {
                            let targets = (lb.targets ?? []).slice().sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0));
                            node.name = lb.name;
                            node.subtitle = `Algorithm: ${lb.algorithm}`;
                            node.meta = [`${targets.length} target${targets.length === 1 ? "" : "s"}`];
                            node.href = InferenceRoutes.LoadBalancerView(lb.id);
                            for (let t of targets)
                                node.children.push({ label: `Weight ${t.weight}`, node: buildTree(t.inferencer_type, t.inferencer_id, depth + 1, nextVisited) });
                        }
                        break;
                    }
                    case "FAILOVER":
                    {
                        let fo = foMap.get(id);
                        if (fo)
                        {
                            let targets = (fo.targets ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                            node.name = fo.name;
                            node.meta = [`${targets.length} target${targets.length === 1 ? "" : "s"}`];
                            node.href = InferenceRoutes.FailoverView(fo.id);
                            for (let i = 0; i < targets.length; i++)
                            {
                                let t = targets[i];
                                node.children.push({ label: `#${t.order ?? i + 1} · ${t.timeout}ms`, node: buildTree(t.inferencer_type, t.inferencer_id, depth + 1, nextVisited) });
                            }
                        }
                        break;
                    }
                    case "RACE":
                    {
                        let race = raceMap.get(id);
                        if (race)
                        {
                            let targets = race.targets ?? [];
                            node.name = race.name;
                            node.meta = [`${targets.length} target${targets.length === 1 ? "" : "s"}`];
                            node.href = InferenceRoutes.RaceView(race.id);
                            for (let i = 0; i < targets.length; i++)
                            {
                                let t = targets[i];
                                node.children.push({ label: `Contender ${i + 1}`, node: buildTree(t.inferencer_type, t.inferencer_id, depth + 1, nextVisited) });
                            }
                        }
                        break;
                    }
                    case "ROUTER":
                    {
                        let rt = routerMap.get(id);
                        if (rt)
                        {
                            let routes = (rt.routes ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                            node.name = rt.name;
                            node.meta = [`${routes.length} route${routes.length === 1 ? "" : "s"}`];
                            node.href = InferenceRoutes.RouterView(rt.id);
                            for (let r of routes)
                                node.children.push({ label: `Route ${r.order}${r.ccg_not ? " · NOT" : ""} · ${r.ccg_type}`, node: buildTree(r.inferencer_type, r.inferencer_id, depth + 1, nextVisited) });
                            if (rt.default_inferencer_id)
                                node.children.push({ label: "Default", node: buildTree(rt.default_inferencer_type, rt.default_inferencer_id, depth + 1, nextVisited) });
                        }
                        break;
                    }
                    case "GATEWAY":
                    {
                        let gw = gatewayMap.get(id);
                        if (gw)
                        {
                            let guardRailsCount = (gw.guard_rails ?? []).length;
                            let knowledgebasesCount = (gw.knowledgebases ?? []).length;
                            let meta: string[] = [];
                            if (guardRailsCount > 0) meta.push(`${guardRailsCount} guard rail${guardRailsCount === 1 ? "" : "s"}`);
                            if (knowledgebasesCount > 0) meta.push(`${knowledgebasesCount} knowledgebase${knowledgebasesCount === 1 ? "" : "s"}`);
                            if (gw.validator_id) meta.push("validator");
                            node.name = gw.name;
                            node.meta = meta;
                            node.href = InferenceRoutes.GatewayView(gw.id);
                            if (gw.inferencer_id)
                                node.children.push({ label: "Inferencer", node: buildTree(gw.inferencer_type, gw.inferencer_id, depth + 1, nextVisited) });
                        }
                        break;
                    }
                }
                return node;
            };

            let roots: InferencerTreeNode[] = [];
            for (let gw of gatewayFulls as GatewayFullRow[])
                if (!referenced.has(key("GATEWAY", gw.id))) roots.push(buildTree("GATEWAY", gw.id, 0, new Set()));
            for (let rt of routerFulls as RouterFullRow[])
                if (!referenced.has(key("ROUTER", rt.id))) roots.push(buildTree("ROUTER", rt.id, 0, new Set()));
            for (let race of raceFulls as RaceFullRow[])
                if (!referenced.has(key("RACE", race.id))) roots.push(buildTree("RACE", race.id, 0, new Set()));
            for (let fo of foFulls as FailoverFullRow[])
                if (!referenced.has(key("FAILOVER", fo.id))) roots.push(buildTree("FAILOVER", fo.id, 0, new Set()));
            for (let lb of lbFulls as LoadBalancerFullRow[])
                if (!referenced.has(key("LOAD_BALANCER", lb.id))) roots.push(buildTree("LOAD_BALANCER", lb.id, 0, new Set()));
            for (let m of models.rows as ModelRow[])
                if (!referenced.has(key("MODEL", m.id))) roots.push(buildTree("MODEL", m.id, 0, new Set()));

            this.setState({ loading: false, roots });
        }
        catch (e: any)
        {
            console.error(e);
            this.setState({ loading: false, error: e?.message ?? "Failed to load" });
        }
    }

    override render(): ReactNode
    {
        return this.props.render({
            roots: this.state.roots,
            loading: this.state.loading,
            error: this.state.error,
        });
    }
}